// CB:BEGIN contractor-escrow-section
/**
 * ContractorEscrowSection — embeds the full escrow dashboard inline
 * on the contractor project details page.
 * Accepts projectId as a prop; no localStorage dependency.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../lib/useApi';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';

interface ContractorEscrowSectionProps {
  projectId: string;
  onProjectComplete?: () => void;
}

interface EscrowAccount {
  id: string;
  projectId: string;
  status: string;
  totalDeposited: number;
  totalReleased: number;
  currentBalance: number;
  pendingRelease: number;
  projectStartDate?: string;
  projectEndDate?: string;
  milestones: EscrowMilestone[];
  changeOrders: EscrowChangeOrder[];
  transactions: EscrowTransaction[];
  notifications: EscrowNotification[];
}

interface EscrowMilestone {
  id: string;
  milestoneName: string;
  milestoneDescription?: string;
  milestoneAmount: number;
  status: string;
  milestoneDueDate?: string;
  milestoneCompletedDate?: string;
  milestoneApprovedDate?: string;
  milestonePhotos: string[];
  milestoneKey?: string;
}

interface EscrowChangeOrder {
  id: string;
  changeOrderDescription: string;
  changeOrderReason?: string;
  changeOrderAmount: number;
  status: string;
  requestedBy: string;
  approvedBy?: string;
  approvedAt?: string;
}

interface EscrowTransaction {
  id: string;
  type: string;
  amount: number;
  description?: string;
  status: string;
  createdAt: string;
}

interface EscrowNotification {
  id: string;
  notificationType: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

const ContractorEscrowSection: React.FC<ContractorEscrowSectionProps> = ({ projectId, onProjectComplete }) => {
  const { getToken } = useAuth();
  const { get, post, patch } = useApi();
  const [escrowAccount, setEscrowAccount] = useState<EscrowAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'notes' | 'message'>('overview');
  const [expandedEvidence, setExpandedEvidence] = useState<Record<string, boolean>>({});
  const [evidenceByMilestone, setEvidenceByMilestone] = useState<Record<string, { evidence: any[] }>>({});
  const [evidenceActionLoading, setEvidenceActionLoading] = useState<string | null>(null);
  const [evidenceSuccess, setEvidenceSuccess] = useState<string | null>(null);
  const [showChangeOrderForm, setShowChangeOrderForm] = useState(false);
  const [changeOrderDesc, setChangeOrderDesc] = useState('');
  const [changeOrderReason, setChangeOrderReason] = useState('');
  const [changeOrderAmount, setChangeOrderAmount] = useState('');
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // Project detail fields for Overview tab
  const [projectRoofColor, setProjectRoofColor] = useState<string | null>(null);
  const [projectScheduledStart, setProjectScheduledStart] = useState<string | null>(null);
  const [contractDocUrl, setContractDocUrl] = useState<string | null>(null); // signed PDF (post-signing)
  const [unsignedContractUrl, setUnsignedContractUrl] = useState<string | null>(null); // unsigned PDF (pre-signing preview)
  const [contractorSigned, setContractorSigned] = useState(false);
  const [contractFullySigned, setContractFullySigned] = useState(false);
  const [depositCaptured, setDepositCaptured] = useState(false);
  const [milestonesConfigured, setMilestonesConfigured] = useState(false);
  const [projectStatus, setProjectStatus] = useState<string | null>(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [signingLoading, setSigningLoading] = useState(false);
  const [signingError, setSigningError] = useState<string | null>(null);

  const fetchEscrowData = async () => {
    setLoading(true);
    try {
      // Use get() so Clerk Bearer token is sent (credentials:'include' alone won't work with Clerk JWT auth)
      let response: any;
      try {
        response = await get<any>(`/api/escrow/${projectId}/dashboard`);
      } catch (escrowErr: any) {
        setEscrowAccount(null);
        setLoading(false);
        return;
      }
      let escrowData = response.escrowAccount || response;
      // Seed project status from escrow dashboard response
      if (response.project?.status) setProjectStatus(response.project.status);
      else if (escrowData.project?.status) setProjectStatus(escrowData.project.status);

      // Fetch phases and project details in parallel
      try {
        let phases: any[] = [];
        try {
          const projectData = await get<{ phases?: any[]; project?: any; roofColor?: string; scheduledStartAt?: string; roofColorSelection?: any; signingEvents?: any[] }>(`/api/projects/${projectId}`);
          const proj = (projectData as any)?.project ?? projectData;
          if (proj?.status) setProjectStatus(proj.status);
          if (proj?.roofColor) setProjectRoofColor(proj.roofColor);
          else if (proj?.roofColorSelection?.snapshotDisplayName) setProjectRoofColor(proj.roofColorSelection.snapshotDisplayName);
          if (proj?.scheduledStartAt) setProjectScheduledStart(proj.scheduledStartAt);
          if (proj?.depositCapturedAt) setDepositCaptured(true);
          if (proj?.contractFullySignedAt) setContractFullySigned(true);
          // Find signed contractor contract from signing events (party is uppercase)
          const contractorSig = Array.isArray(proj?.signingEvents)
            ? proj.signingEvents.find((e: any) => String(e.party).toUpperCase() === 'CONTRACTOR')
            : null;
          if (contractorSig) setContractorSigned(true);
          // Fetch contract download URL from documents endpoint
          get<{ documents?: any[] }>(`/api/projects/${projectId}/documents`)
            .then((d: any) => {
              const contractDoc = (d?.documents ?? []).find((doc: any) => doc.type === 'GENERATED_CONTRACT_PDF' && doc.downloadUrl);
              if (contractDoc?.downloadUrl) setContractDocUrl(contractDoc.downloadUrl);
            })
            .catch(() => {});
          if (projectData?.phases && Array.isArray(projectData.phases)) {
            phases = projectData.phases;
            if (phases.length > 0) setMilestonesConfigured(true);
          } else if (proj?.phases && Array.isArray(proj.phases)) {
            phases = proj.phases;
            if (phases.length > 0) setMilestonesConfigured(true);
          }
        } catch {
          try {
            const phasesData = await get<{ phases?: any[] }>(`/api/admin/projects/${projectId}/phases`);
            phases = phasesData?.phases || (Array.isArray(phasesData) ? phasesData : []);
          } catch { /* non-critical */ }
        }

        if (phases.length > 0) {
          const phaseToKey: Record<number, string> = { 1: 'M1_START', 2: 'M2_DEMO', 3: 'M3_COMPLETE' };
          const canonicalStatusMap: Record<string, string> = {};
          if (Array.isArray(escrowData.milestones)) {
            for (const m of escrowData.milestones as any[]) {
              if (m.milestoneKey) canonicalStatusMap[m.milestoneKey] = m.status;
            }
          }
          const milestones = phases.map((phase: any) => {
            const milestoneKey = phaseToKey[phase.number];
            return {
              id: phase.id || `phase-${phase.number}`,
              milestoneName: phase.name || `Milestone ${phase.number}`,
              milestoneDescription: phase.notes || undefined,
              milestoneAmount: phase.amountCents || 0,
              status: (milestoneKey ? canonicalStatusMap[milestoneKey] : null) || phase.status || 'PENDING',
              milestoneKey: milestoneKey || undefined,
              milestoneDueDate: phase.dueDate || undefined,
              milestoneCompletedDate: phase.submittedAt || phase.approvedAt || undefined,
              milestoneApprovedDate: phase.approvedAt || undefined,
              milestonePhotos: phase.photos || [],
            };
          });
          escrowData = { ...escrowData, milestones };

          // Fetch evidence per milestone (non-critical)
          try {
            const evResults = await Promise.all(
              milestones
                .filter((m: any) => m.milestoneKey)
                .map((m: any) =>
                  get(`/api/projects/${projectId}/milestones/${m.milestoneKey}/evidence`)
                    .then((r: any) => ({ key: m.milestoneKey, evidence: r?.evidence ?? [] }))
                    .catch(() => ({ key: m.milestoneKey, evidence: [] }))
                )
            );
            const evMap: Record<string, { evidence: any[] }> = {};
            (evResults as Array<{ key: string; evidence: any[] }>).forEach(({ key, evidence }) => {
              evMap[key] = { evidence };
            });
            setEvidenceByMilestone(evMap);
          } catch { /* non-critical */ }
        }
      } catch { /* non-critical */ }

      setEscrowAccount(escrowData);
    } catch {
      setEscrowAccount(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) fetchEscrowData();
  }, [projectId]);

  const formatCurrency = (cents: number | null | undefined) => {
    if (cents == null || isNaN(Number(cents))) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(cents) / 100);
  };

  const getStatusColor = (status: string) => {
    switch (String(status).toUpperCase()) {
      case 'FUNDED': case 'ACTIVE': return '#10b981';
      case 'COMPLETED': case 'RELEASED': return '#059669';
      case 'LOCKED': return '#3b82f6';
      case 'RELEASE_PENDING': return '#f59e0b';
      case 'DISPUTED': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (String(status).toUpperCase()) {
      case 'CREATED': return 'Awaiting payment';
      case 'FUNDED': return 'Holding funds';
      case 'LOCKED': return 'Locked';
      case 'RELEASE_PENDING': return 'Release pending';
      case 'RELEASED': return 'Released';
      case 'REFUNDED': return 'Refunded';
      case 'DISPUTED': return 'Disputed';
      case 'ACTIVE': return 'Active';
      case 'COMPLETED': return 'Completed';
      case 'CANCELLED': return 'Cancelled';
      default: return (status || '').replace(/_/g, ' ');
    }
  };

  const getMilestoneStatusColor = (status: string) => {
    switch (String(status).toUpperCase()) {
      case 'PENDING': return '#f59e0b';
      case 'IN_PROGRESS': case 'EVIDENCE_SUBMITTED': case 'CONTRACTOR_SUBMITTED': return '#3b82f6';
      case 'APPROVED': case 'HOMEOWNER_APPROVED': return '#059669';
      case 'RELEASED': case 'ADMIN_RELEASED': case 'COMPLETED': return '#10b981';
      default: return '#6b7280';
    }
  };

  const handleEvidenceUpload = async (milestoneKey: string) => {
    const input = fileInputRefs.current[milestoneKey];
    if (!input?.files?.length) return;
    setEvidenceActionLoading(`evidence-${milestoneKey}`);
    setEvidenceSuccess(null);
    try {
      const token = await getToken();
      const form = new FormData();
      for (let i = 0; i < input.files.length; i++) form.append('files', input.files[i]);
      const res = await fetch(getApiUrl(`/api/projects/${projectId}/milestones/${milestoneKey}/evidence/upload`), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any)?.error ?? `Upload failed (${res.status})`);
      }
      input.value = '';
      setEvidenceSuccess(milestoneKey);
      await fetchEscrowData();
    } catch (error: any) {
      alert(error?.message ?? 'Failed to upload evidence');
    } finally {
      setEvidenceActionLoading(null);
    }
  };

  const handleChangeOrderSubmit = async () => {
    if (!changeOrderDesc.trim() || !changeOrderAmount) return;
    try {
      await patch(`/api/escrow/${escrowAccount?.projectId}/change-order`, {
        description: changeOrderDesc,
        reason: changeOrderReason,
        amount: Math.round(parseFloat(changeOrderAmount) * 100),
        requestedBy: 'CONTRACTOR',
      });
      setShowChangeOrderForm(false);
      setChangeOrderDesc('');
      setChangeOrderReason('');
      setChangeOrderAmount('');
      await fetchEscrowData();
    } catch (error) {
      console.error('Error creating change order:', error);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-card" style={{ marginBottom: 24, textAlign: 'center', padding: '32px', color: '#6b7280' }}>
        Loading escrow data…
      </div>
    );
  }

  if (!escrowAccount) return null;

  const milestones = escrowAccount.milestones || [];
  const releasedCount = milestones.filter((m) =>
    ['COMPLETED', 'HOMEOWNER_APPROVED', 'RELEASED', 'ADMIN_RELEASED'].includes(String(m.status).toUpperCase())
  ).length;

  // "Mark project complete" is only available once milestone 3 (M3_COMPLETE) has been paid out.
  const milestone3 = milestones.find((m: any) => m.milestoneKey === 'M3_COMPLETE' || m.milestoneName?.includes('3') || milestones.indexOf(m) === 2);
  const milestone3PaidOut = milestone3
    ? ['RELEASED', 'ADMIN_RELEASED'].includes(String(milestone3.status).toUpperCase())
    : false;

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px 0', fontSize: 20, fontWeight: 700 }}>Project Escrow</h3>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>Track project payments and milestone progress</p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="dashboard-card" style={{ textAlign: 'center', padding: '16px 12px' }}>
          <div style={{ color: (escrowAccount.currentBalance ?? 0) > 0 ? '#10b981' : '#6b7280', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{formatCurrency(escrowAccount.currentBalance ?? 0)}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>Pending release to you</div>
        </div>
        <div className="dashboard-card" style={{ textAlign: 'center', padding: '16px 12px' }}>
          <div style={{ color: '#3b82f6', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{formatCurrency(escrowAccount.totalDeposited ?? 0)}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>Total in escrow</div>
        </div>
        <div className="dashboard-card" style={{ textAlign: 'center', padding: '16px 12px' }}>
          <div style={{ color: '#10b981', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{formatCurrency(escrowAccount.totalReleased ?? 0)}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>Total earned (released to you)</div>
        </div>
        <div className="dashboard-card" style={{ textAlign: 'center', padding: '16px 12px' }}>
          <div style={{ color: '#8b5cf6', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{releasedCount} / {milestones.length}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>Milestones released</div>
        </div>
        {/* Platform fee card — always shown so contractor understands the full breakdown */}
        <div className="dashboard-card" style={{ textAlign: 'center', padding: '16px 12px', borderTop: '3px solid #e5e7eb' }}>
          <div style={{ color: '#6b7280', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{formatCurrency((escrowAccount as any).platformFeeCents ?? 0)}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>CoreBids platform fee</div>
          <div style={{ marginTop: 4, fontSize: 11, color: (escrowAccount as any).platformFeePaidOut ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
            {(escrowAccount as any).platformFeePaidOut ? '✓ Paid to CoreBids' : 'Pending payout to CoreBids'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-card">
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 20, overflowX: 'auto' }}>
          {([
            { key: 'overview', label: 'Overview' },
            { key: 'transactions', label: 'Payments' },
            { key: 'notes', label: 'Notes' },
            { key: 'message', label: 'Message Us' },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
                color: activeTab === tab.key ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === tab.key ? 600 : 400,
                cursor: 'pointer',
                fontSize: 15,
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Escrow summary */}
            <div style={{ padding: '12px 16px', background: '#f9fafb', borderRadius: 8, border: `2px solid ${getStatusColor(escrowAccount.status)}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Escrow</div>
                {projectStatus !== 'COMPLETED' && (
                  <button
                    onClick={async () => {
                      if (!milestone3PaidOut) return;
                      if (!confirm('Mark this project as complete? This will notify the homeowner and contractor by email.')) return;
                      setMarkingComplete(true);
                      try {
                        await post(`/api/projects/${projectId}/complete`, {});
                        setProjectStatus('COMPLETED');
                        onProjectComplete?.();
                      } catch (err: any) {
                        alert(err?.message ?? 'Failed to mark project complete');
                      } finally {
                        setMarkingComplete(false);
                      }
                    }}
                    disabled={markingComplete || !milestone3PaidOut}
                    title={!milestone3PaidOut ? 'Milestone 3 must be completed and paid out before marking the project complete' : ''}
                    style={{
                      fontSize: 12,
                      padding: '5px 12px',
                      border: `1px solid ${milestone3PaidOut ? '#16a34a' : '#d1d5db'}`,
                      borderRadius: 6,
                      background: milestone3PaidOut ? '#f0fdf4' : '#f3f4f6',
                      color: milestone3PaidOut ? '#16a34a' : '#9ca3af',
                      fontWeight: 600,
                      cursor: milestone3PaidOut ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {markingComplete ? 'Saving…' : 'Mark project complete'}
                  </button>
                )}
                {projectStatus === 'COMPLETED' && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a' }}>✓ Project complete</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: '#374151' }}>
                Status:{' '}
                {projectStatus === 'COMPLETED' ? (
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>Completed</span>
                ) : (
                  <span style={{ color: getStatusColor(escrowAccount.status), fontWeight: 600 }}>{getStatusLabel(escrowAccount.status)}</span>
                )}
                {' · '}Balance: <span style={{ fontWeight: 600 }}>{formatCurrency(escrowAccount.currentBalance ?? 0)}</span>
              </div>
            </div>

            {/* Start date */}
            <div style={{ padding: '12px 16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Start date</div>
              <div style={{ fontSize: 13, color: '#374151' }}>
                {(() => {
                  const d = projectScheduledStart ?? escrowAccount.projectStartDate ?? null;
                  return d
                    ? <>Scheduled by homeowner: <span style={{ fontWeight: 600 }}>{new Date(d).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span></>
                    : 'Not yet scheduled';
                })()}
              </div>
            </div>

            {/* Contract */}
            <div style={{ padding: '12px 16px', background: '#f9fafb', borderRadius: 8, border: contractorSigned || contractFullySigned ? '1px solid #bbf7d0' : '1px solid #fde68a' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Contract</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {/* Signed: show "View signed contract", hide sign button */}
                  {contractorSigned ? (
                    <button
                      onClick={async () => {
                        try {
                          const token = await getToken();
                          const res = await fetch(getApiUrl(`/api/projects/${projectId}/contract/contractor-pdf`), {
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                            credentials: 'include',
                          });
                          if (res.ok) {
                            const data = await res.json();
                            if (data.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
                          }
                        } catch { /* ignore */ }
                      }}
                      style={{ fontSize: 12, padding: '3px 10px', border: '1px solid #16a34a', borderRadius: 6, color: '#16a34a', fontWeight: 600, background: '#f0fdf4', cursor: 'pointer' }}
                    >
                      View signed contract
                    </button>
                  ) : (
                    <>
                      {/* Unsigned: show "View contract" + "Sign contract" */}
                      <button
                        onClick={async () => {
                          try {
                            let url = unsignedContractUrl;
                            if (!url) {
                              const res = await get<{ signedUrl?: string }>(`/api/projects/${projectId}/contract/contractor-pdf`);
                              url = (res as any)?.signedUrl ?? null;
                              if (url) setUnsignedContractUrl(url);
                            }
                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                          } catch { /* ignore */ }
                        }}
                        style={{ fontSize: 12, padding: '3px 10px', border: '1px solid #d1d5db', borderRadius: 6, color: '#374151', fontWeight: 500, cursor: 'pointer', background: '#fff' }}
                      >
                        View contract
                      </button>
                      <button
                        onClick={async () => {
                          setSigningError(null);
                          const confirmed = window.confirm('By clicking OK you confirm you have reviewed and agree to the contract terms. This will digitally sign the contract on your behalf.');
                          if (!confirmed) return;
                          setSigningLoading(true);
                          try {
                            await post(`/api/projects/${projectId}/sign`, { party: 'CONTRACTOR' });
                            setContractorSigned(true);
                            // Fetch signed PDF URL
                            get<{ documents?: any[] }>(`/api/projects/${projectId}/documents`)
                              .then((d: any) => {
                                const doc = (d?.documents ?? []).find((x: any) => x.type === 'GENERATED_CONTRACT_PDF' && x.downloadUrl);
                                if (doc?.downloadUrl) setContractDocUrl(doc.downloadUrl);
                              })
                              .catch(() => {});
                          } catch (err: any) {
                            setSigningError(err?.message ?? 'Failed to sign contract');
                          } finally {
                            setSigningLoading(false);
                          }
                        }}
                        disabled={signingLoading}
                        style={{ fontSize: 12, padding: '3px 10px', border: '1px solid #2563eb', borderRadius: 6, background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                      >
                        {signingLoading ? 'Signing…' : 'Sign contract'}
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#374151', marginBottom: 2 }}>
                Contractor signed: <span style={{ fontWeight: 600 }}>{contractorSigned ? 'Yes' : 'No'}</span>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {contractFullySigned ? 'Contract fully signed.' : contractorSigned ? 'Awaiting homeowner signature.' : 'Not yet signed.'}
              </div>
              {signingError && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{signingError}</div>}
            </div>

            {/* Roof color */}
            <div style={{ padding: '12px 16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Roof color</div>
              <div style={{ fontSize: 13, color: '#374151' }}>
                Selected by homeowner: <span style={{ fontWeight: 600 }}>{projectRoofColor ?? 'Not yet selected'}</span>
              </div>
            </div>

            {/* Start project gate checklist */}
            <div style={{ padding: '12px 16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Start project</div>
              {(() => {
                const escrowFunded = ['FUNDED', 'ACTIVE', 'IN_PROGRESS', 'PARTIALLY_RELEASED', 'RELEASED', 'COMPLETED'].includes(String(escrowAccount.status).toUpperCase());
                return [
                  { label: 'Deposit captured', done: depositCaptured || escrowFunded },
                  { label: 'Roof color selected', done: !!projectRoofColor },
                  { label: 'Contract signed', done: contractorSigned || contractFullySigned },
                  { label: 'Milestone 1 (Project Start) defined', done: milestonesConfigured || milestones.length > 0 },
                  { label: 'Scheduled start date set', done: !!(projectScheduledStart ?? escrowAccount.projectStartDate) },
                ];
              })().map(({ label, done }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: done ? '#16a34a' : '#9ca3af', fontSize: 15 }}>{done ? '✓' : '○'}</span>
                  <span style={{ color: done ? '#374151' : '#6b7280' }}>{label}{done ? ': ✓' : ''}</span>
                </div>
              ))}
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, marginBottom: 0 }}>
                Work and payments through CoreBids; going off-platform may void guarantees and dispute support.{' '}
                <a href="#" style={{ color: '#2563eb' }} onClick={(e) => { e.preventDefault(); setActiveTab('transactions'); }}>Payment terms</a>
              </p>
            </div>

            {/* Milestone evidence summary — gated behind contract signing */}
            {milestones.length > 0 && (
              <div>
                {!contractorSigned && !contractFullySigned && (
                  <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 10, fontSize: 13, color: '#92400e' }}>
                    You must sign the contract above before submitting milestone evidence.
                  </div>
                )}
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Milestone Evidence</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {milestones.map((m) => {
                    const mKey = m.milestoneKey ?? m.id;
                    const evidence = evidenceByMilestone[m.milestoneKey ?? '']?.evidence ?? [];
                    const isReleased = ['RELEASED', 'ADMIN_RELEASED', 'COMPLETED', 'HOMEOWNER_APPROVED'].includes(String(m.status).toUpperCase());
                    const fileCount = evidence.reduce((acc: number, ev: any) => acc + (ev.files?.length ?? 1), 0);
                    const statusColor = getMilestoneStatusColor(m.status);
                    const payoutDate = evidence.find((e: any) => e.approvedAt || e.releasedAt)?.approvedAt
                      ?? evidence.find((e: any) => e.approvedAt || e.releasedAt)?.releasedAt ?? null;
                    const isExpanded = expandedEvidence[mKey] ?? false;
                    return (
                      <div key={m.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{m.milestoneName}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>{formatCurrency(m.milestoneAmount)}</span>
                            {evidence.length > 0 && (
                              <button
                                onClick={() => setExpandedEvidence(prev => ({ ...prev, [mKey]: !isExpanded }))}
                                style={{ fontSize: 12, padding: '3px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#374151', fontWeight: 500 }}
                              >
                                {isExpanded ? 'Hide evidence' : `View evidence (${fileCount})`}
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: statusColor + '20', color: statusColor, textTransform: 'uppercase' }}>
                            {isReleased ? 'Released' : m.status.replace(/_/g, ' ')}
                          </span>
                          {isReleased && <span style={{ fontSize: 13 }}>✅</span>}
                          {isReleased && (
                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                              {formatCurrency(m.milestoneAmount)} paid out
                              {payoutDate ? ` · ${new Date(payoutDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}` : ''}
                            </span>
                          )}
                        </div>
                        {isExpanded && evidence.length > 0 && (
                          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
                            {evidence.map((ev: any, idx: number) => (
                              <div key={ev.id ?? idx} style={{ fontSize: 12, color: '#374151', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: ev.status === 'APPROVED' ? '#16a34a' : ev.status === 'PENDING_APPROVAL' ? '#2563eb' : '#9ca3af', fontWeight: 600 }}>
                                  {ev.status === 'APPROVED' ? '✓' : ev.status === 'PENDING_APPROVAL' ? '⏳' : '·'}
                                </span>
                                <span>{ev.status?.replace(/_/g, ' ') ?? 'Submitted'}</span>
                                {ev.submittedAt && <span style={{ color: '#9ca3af' }}>· {new Date(ev.submittedAt).toLocaleDateString()}</span>}
                                {Array.isArray(ev.files) && ev.files.map((f: any, fi: number) => (
                                  <a key={fi} href={f.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>
                                    file {fi + 1}
                                  </a>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Submit evidence controls — gated behind contract signing */}
                        {!isReleased && m.milestoneKey && (
                          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
                            {(contractorSigned || contractFullySigned) ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <input
                                  ref={(el) => { fileInputRefs.current[m.milestoneKey!] = el; }}
                                  type="file"
                                  multiple
                                  accept=".pdf,.jpg,.jpeg,.png,.heic"
                                  style={{ display: 'none' }}
                                  onChange={() => handleEvidenceUpload(m.milestoneKey!)}
                                />
                                <button
                                  onClick={() => fileInputRefs.current[m.milestoneKey!]?.click()}
                                  disabled={evidenceActionLoading === `evidence-${m.milestoneKey}`}
                                  style={{
                                    fontSize: 12,
                                    padding: '5px 12px',
                                    border: '1px solid #2563eb',
                                    borderRadius: 6,
                                    background: '#eff6ff',
                                    color: '#2563eb',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {evidenceActionLoading === `evidence-${m.milestoneKey}` ? 'Uploading…' : '+ Submit Evidence'}
                                </button>
                                {evidenceSuccess === m.milestoneKey && (
                                  <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ Evidence submitted</span>
                                )}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#92400e' }}>
                                <span>🔒</span>
                                <span>Sign the contract above to unlock evidence submission</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}


          </div>
        )}

        {/* Payments tab */}
        {activeTab === 'transactions' && (
          <div>
            <h4 style={{ marginBottom: 16 }}>Payment History</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, fontSize: 13 }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, fontSize: 13 }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, fontSize: 13 }}>Description</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, fontSize: 13 }}>Amount</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, fontSize: 13 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(escrowAccount.transactions || []).length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: '20px 12px', color: '#6b7280', textAlign: 'center' }}>No payment history yet.</td></tr>
                  ) : (
                    (escrowAccount.transactions || []).map((tx) => (
                      <tr key={tx.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '10px 12px', fontSize: 14 }}>{new Date(tx.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding: '10px 12px', fontSize: 14, textTransform: 'capitalize' }}>{tx.type.toLowerCase()}</td>
                        <td style={{ padding: '10px 12px', fontSize: 14 }}>{tx.description || '—'}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: tx.type === 'RELEASE' ? '#10b981' : '#dc2626' }}>
                          {tx.type === 'RELEASE' ? '+' : '−'}{formatCurrency(tx.amount)}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: tx.status === 'SUCCEEDED' ? '#10b98120' : tx.status === 'FAILED' ? '#dc262620' : '#f59e0b20', color: tx.status === 'SUCCEEDED' ? '#10b981' : tx.status === 'FAILED' ? '#dc2626' : '#f59e0b', textTransform: 'uppercase' }}>
                            {tx.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notes tab */}
        {activeTab === 'notes' && (
          <div>
            <h4 style={{ marginBottom: 12 }}>Project Notes</h4>
            <textarea
              className="form-control"
              rows={8}
              placeholder="Keep track of project details, reminders, and notes here…"
              defaultValue={(() => {
                try { return localStorage.getItem(`contractor_notes_${projectId}`) ?? ''; } catch { return ''; }
              })()}
              onChange={(e) => {
                try { localStorage.setItem(`contractor_notes_${projectId}`, e.target.value); } catch { /* ignore */ }
              }}
              style={{ width: '100%', resize: 'vertical', fontSize: 14 }}
            />
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>Notes are saved locally on this device.</p>
          </div>
        )}

        {/* Message Us tab */}
        {activeTab === 'message' && (
          <div style={{ maxWidth: 480 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Contact Support</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>support@corebids.com</div>
              </div>
            </div>
            <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
              Have a question about this project? Our team typically responds within one business day.
            </p>
            <a
              href={`mailto:support@corebids.com?subject=${encodeURIComponent(`Project ${projectId} — Support Request`)}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 14px',
                background: '#2563eb',
                color: '#fff',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'background 0.15s',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              Send email
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractorEscrowSection;
// CB:END contractor-escrow-section
